import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, MoreHorizontal, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Breadcrumbs = ({ pages }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [completedPages, setCompletedPages] = useState([]);
  const [isCloneOrDraft, setIsCloneOrDraft] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const { t } = useTranslation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isExampleMode = location.state?.isExample;
  const dropdownRef = useRef(null);
  const moreButtonRef = useRef(null);

  const [showMobileDropdown, setShowMobileDropdown] = useState(false);
  const currentPageIndex = pages.findIndex(page => page.link === location.pathname);
  const currentPage = currentPageIndex !== -1 ? pages[currentPageIndex] : null;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          (!moreButtonRef.current || !moreButtonRef.current.contains(event.target))) {
        setShowDropdown(false);
        setShowMobileDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const cloneInfo = sessionStorage.getItem('cloneInfo');
    setIsCloneOrDraft(!!(cloneInfo));
  }, [location.pathname]);

  useEffect(() => {
    setShowDropdown(false);
    setShowMobileDropdown(false);
  }, [location.pathname]);

  useEffect(() => {
    const checkCompletedPages = () => {
      const completed = ['/form/collaborators'];
      
      if (sessionStorage.getItem('employees')) {
        completed.push('/form/general');
      }
      
      if (sessionStorage.getItem('general')) {
        completed.push('/form/schedules');
      }
      
      if (sessionStorage.getItem('schedules')) {
        const schedules = JSON.parse(sessionStorage.getItem('schedules') || '[]');
        
        const userCreatedSchedules = schedules.filter(s => {
          const predefinedCodes = ['FF', 'FE', 'FR', 'Abs', 'Cmp', 'HF', 'XX', 'YY'];
          return !predefinedCodes.includes(s.code);
        });
        
        if (userCreatedSchedules.length > 0) {
          completed.push('/form/schedule');
          completed.push('/form/legislation');
        }
      }

      if (sessionStorage.getItem('legislation')) {
        completed.push('/form/absences');
      }

      if (sessionStorage.getItem('absences')) {
        completed.push('/form/availability');
      }

      if(sessionStorage.getItem('shiftRestrictions')){
        completed.push('/form/transitions');
      }

      if(sessionStorage.getItem('penalizedTransitions') || 
        sessionStorage.getItem('penalizedTransitions_3') ||
        sessionStorage.getItem('penalizedTransitions_4') ||
        sessionStorage.getItem('penalizedTransitions_5'))
      {
        completed.push('/form/staff');
      }
      
      if (sessionStorage.getItem('staffRequirements')) {
        completed.push('/form/distribution');
      }
    
      if (sessionStorage.getItem('distributions')) {
        completed.push('/form/sseq');
      }
      
      if (sessionStorage.getItem('shiftSequenceParams')) {
        completed.push('/form/wsum');
      }
      
      if (sessionStorage.getItem('weeklySumParams')) {
        completed.push('/form/generate');
      }
      
      setCompletedPages(completed);
    };
    
    checkCompletedPages();
  }, [location.pathname]);

  const getVisiblePages = () => {
    const currentPageIndex = pages.findIndex(page => page.link === location.pathname);
    
    if (windowWidth < 768) {
      if (currentPageIndex <= 1) return pages.slice(0, 3);
      if (currentPageIndex >= pages.length - 2) return pages.slice(-3);
      return [pages[0], pages[currentPageIndex], pages[pages.length - 1]];
    }
    
    if (windowWidth < 1200) {
      if (currentPageIndex <= 2) return pages.slice(0, 5);
      if (currentPageIndex >= pages.length - 3) return pages.slice(-5);
      return [
        pages[0],
        pages[1],
        pages[currentPageIndex],
        pages[pages.length - 2],
        pages[pages.length - 1]
      ];
    }
    
    const firstGroup = pages.slice(0, 3);
    const lastGroup = pages.slice(-3);
    
    if (currentPageIndex < 3) return [...pages.slice(0, 5), { isMore: true }, lastGroup[lastGroup.length - 1]];
    if (currentPageIndex >= pages.length - 3) return [firstGroup[0], { isMore: true }, ...pages.slice(-5)];
    
    return [
      firstGroup[0],
      { isMore: true },
      pages[currentPageIndex - 1],
      pages[currentPageIndex],
      pages[currentPageIndex + 1],
      { isMore: true },
      lastGroup[lastGroup.length - 1]
    ];
  };

  const isPageNavigable = (pageLink) => {
    if (location.pathname === pageLink) {
      return true;
    }
    
    if (isCloneOrDraft) {
      return true;
    }
    
    return completedPages.includes(pageLink);
  };

  const handlePageClick = (pageLink) => {
    if (isPageNavigable(pageLink)) {
      const isExampleMode = location.state?.isExample;
      navigate(pageLink, { state: { isExample: isExampleMode } });
    }
  };

  const handleMoreClick = (e) => {
    e.stopPropagation();
    setDropdownPosition({ x: e.clientX, y: e.clientY + 20 });
    setShowDropdown(prev => !prev);
  };

  const handleMobileDropdownToggle = (e) => {
    e.stopPropagation();
    setShowMobileDropdown(prev => !prev);
  };

  const visiblePages = getVisiblePages();

  if (isExampleMode) {
    return null;
  }

  if (windowWidth < 640) {
    return (
      <div className="sticky top-0 z-40 w-full bg-gray-100 shadow-sm">
        <div className="px-4 py-3">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={handleMobileDropdownToggle}
              className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none"
            >
              <span className="truncate max-w-[180px]">
                {currentPage ? t(currentPage.translationKey || `pages.${currentPage.name.toLowerCase().replace(/\s+/g, '')}`) : t('pages.selectStep')}
              </span>
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>

            {showMobileDropdown && (
              <div className="absolute left-0 z-10 w-full mt-1 bg-white rounded-md shadow-lg max-h-60 overflow-auto">
                {pages.map((page, index) => {
                  const navigable = isPageNavigable(page.link);
                  const translationKey = `pages.${page.name.toLowerCase().replace(/\s+/g, '')}`;
                  const current = location.pathname === page.link;
                  
                  return (
                    <div 
                      key={index}
                      onClick={() => {
                        if (navigable) {
                          handlePageClick(page.link);
                          setShowMobileDropdown(false);
                        }
                      }}
                      className={`
                        px-4 py-3 text-sm border-b last:border-0
                        ${current ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'} 
                        ${navigable ? 'cursor-pointer hover:bg-gray-100' : 'opacity-70 cursor-not-allowed'}
                      `}
                      title={!navigable ? t("form.general.completeSteps") : ""}
                    >
                      {t(page.translationKey || translationKey)}
                      {current && <span className="ml-2 text-blue-600">•</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (windowWidth < 768) {
    return (
      <div className="flex justify-center bg-gray-100 shadow-sm overflow-x-auto">
        <div className="px-3 py-2">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1">
              <li className="inline-flex items-center">
                <span
                  onClick={() => handlePageClick(pages[0].link)}
                  className={`
                    inline-flex items-center text-sm font-medium px-2 py-1 rounded-md
                    ${location.pathname === pages[0].link ? 'text-white bg-blue-600' : 'text-gray-700'}
                    ${isPageNavigable(pages[0].link) ? 'cursor-pointer hover:bg-gray-200' : 'opacity-70 cursor-not-allowed'}
                  `}
                >
                  {t(pages[0].translationKey || `pages.${pages[0].name.toLowerCase().replace(/\s+/g, '')}`)}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </li>

              {currentPageIndex > 0 && currentPageIndex < pages.length - 1 && (
                <li className="inline-flex items-center">
                  <span
                    className="inline-flex items-center text-sm font-medium px-2 py-1 rounded-md text-white bg-blue-600"
                  >
                    {t(currentPage.translationKey || `pages.${currentPage.name.toLowerCase().replace(/\s+/g, '')}`)}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </li>
              )}

              <li className="inline-flex items-center" ref={moreButtonRef}>
                <button
                  onClick={handleMobileDropdownToggle}
                  className="flex items-center px-2 py-1 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <MoreHorizontal className="h-5 w-5" />
                </button>
                {currentPageIndex !== pages.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}

                {showMobileDropdown && (
                  <div 
                    className="absolute z-10 mt-2 w-56 bg-white rounded-md shadow-lg py-1 right-4 top-10"
                    ref={dropdownRef}
                  >
                    {pages.map((page, index) => {
                      const navigable = isPageNavigable(page.link);
                      const translationKey = `pages.${page.name.toLowerCase().replace(/\s+/g, '')}`;
                      const current = location.pathname === page.link;
                      
                      return (
                        <div 
                          key={index}
                          onClick={() => {
                            if (navigable) {
                              handlePageClick(page.link);
                              setShowMobileDropdown(false);
                            }
                          }}
                          className={`
                            px-4 py-2 text-sm
                            ${current ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'} 
                            ${navigable ? 'cursor-pointer hover:bg-gray-100' : 'opacity-70 cursor-not-allowed'}
                          `}
                          title={!navigable ? t("form.general.completeSteps") : ""}
                        >
                          {t(page.translationKey || translationKey)}
                        </div>
                      );
                    })}
                  </div>
                )}
              </li>

              {pages.length > 1 && (
                <li className="inline-flex items-center">
                  <span
                    onClick={() => handlePageClick(pages[pages.length - 1].link)}
                    className={`
                      inline-flex items-center text-sm font-medium px-2 py-1 rounded-md
                      ${location.pathname === pages[pages.length - 1].link ? 'text-white bg-blue-600' : 'text-gray-700'}
                      ${isPageNavigable(pages[pages.length - 1].link) ? 'cursor-pointer hover:bg-gray-200' : 'opacity-70 cursor-not-allowed'}
                    `}
                  >
                    {t(pages[pages.length - 1].translationKey || `pages.${pages[pages.length - 1].name.toLowerCase().replace(/\s+/g, '')}`)}
                  </span>
                </li>
              )}
            </ol>
          </nav>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center bg-gray-100 shadow-sm">
      <div className="px-2 py-2 overflow-x-auto whitespace-nowrap scrollbar-hide">
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1">
            {visiblePages.map((page, index) => {
              if (page.isMore) {
                return (
                  <li key={`more-${index}`} className="inline-flex items-center">
                    <button
                      onClick={(e) => handleMoreClick(e)}
                      className="flex items-center px-2 py-1 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                      ref={moreButtonRef}
                    >
                      <MoreHorizontal className="h-5 w-5" />
                    </button>
                    {index !== visiblePages.length - 1 && (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </li>
                );
              }
              
              const navigable = isPageNavigable(page.link);
              const translationKey = `pages.${page.name.toLowerCase().replace(/\s+/g, '')}`;
              const current = location.pathname === page.link;
              
              return (
                <li key={index} className="inline-flex items-center">
                  <span
                    onClick={() => handlePageClick(page.link)}
                    className={`inline-flex items-center text-sm font-medium px-2 py-1 rounded-md
                      ${current ? 'text-white bg-blue-600' : 'text-gray-700'}
                      ${navigable ? 'cursor-pointer hover:bg-gray-200' : 'opacity-70 cursor-not-allowed'}
                    `}
                    title={!navigable ? t("form.general.completeSteps") : ""}
                  >
                    {t(page.translationKey || translationKey)}
                  </span>
                  {index !== visiblePages.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>

      {showDropdown && (
        <div 
          className="absolute bg-white shadow-lg rounded-md z-50 py-1"
          style={{ top: dropdownPosition.y, left: dropdownPosition.x }}
          ref={dropdownRef}
        >
          {pages.map((page, index) => {
            const navigable = isPageNavigable(page.link);
            const translationKey = `pages.${page.name.toLowerCase().replace(/\s+/g, '')}`;
            const current = location.pathname === page.link;
            
            return (
              <div 
                key={index}
                onClick={() => {
                  if (navigable) {
                    handlePageClick(page.link);
                    setShowDropdown(false);
                  }
                }}
                className={`px-4 py-2 text-sm ${current ? 'bg-blue-50 text-blue-700' : 'text-gray-700'} 
                  ${navigable ? 'cursor-pointer hover:bg-gray-100' : 'opacity-70 cursor-not-allowed'}`}
                title={!navigable ? t("form.general.completeSteps") : ""}
              >
                {t(page.translationKey || translationKey)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Breadcrumbs;